import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { Nav, BgDecorations, GlobalStyles, BtnPrimary, FSectionLabel, MedSelect } from "../components/UI";
import { Toast, useToast } from "../hooks/useToast";
import { processPhoto } from "../utils/imageUtils";
import {
  BLOOD_GROUP_OPTIONS,
  DIABETES_OPTIONS,
  BLOOD_PRESSURE_OPTIONS,
  GENDER_OPTIONS,
} from "../utils/medicalOptions";
import {
  ACCENT, ACCENT2, BORDER, MUTED,
  CARD_COLORS, EMPTY_CARD, generateMemberId,
} from "../utils/constants";

/* ─── Validation ────────────────────────────────────────────── */
function validateCard(c) {
  const e = {};
  if (!c.name?.trim())             e.name            = "Required";
  if (!c.dob)                      e.dob             = "Required";
  if (!c.bloodGroup)               e.bloodGroup      = "Please select a blood group";
  if (!c.ec1Name?.trim())          e.ec1Name         = "Required";
  if (!c.ec1Phone?.trim())         e.ec1Phone        = "Required";
  if (!c.phone?.trim())            e.phone           = "Required";
  if (!c.shippingAddress?.trim())  e.shippingAddress = "Required";
  if (!/^\d{6}$/.test(c.pincode)) e.pincode         = "Must be 6 digits";
  if (!c.accepted)                 e.accepted        = "Required";
  return e;
}

/* ─── Input ─────────────────────────────────────────────────── */
const Inp = ({ err, style, ...p }) => (
  <input style={{
    width: "100%", padding: "11px 13px",
    background: "rgba(255,255,255,0.07)",
    border: `1px solid ${err ? "rgba(231,76,60,0.6)" : "rgba(255,255,255,0.12)"}`,
    borderRadius: 9, color: "#F0EEF8",
    fontSize: 14, fontFamily: "DM Sans, sans-serif",
    outline: "none", boxSizing: "border-box", ...style,
  }} {...p} />
);

/* ─── Field row wrapper ─────────────────────────────────────── */
const Row = ({ label, required, error, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label style={{ fontSize: 12, fontWeight: 500, color: error ? "#e74c3c" : "rgba(240,238,248,0.6)" }}>
      {label}
      {required && <span style={{ color: "#e74c3c" }}> *</span>}
      {hint  && <span style={{ color: "rgba(240,238,248,0.35)", fontWeight: 400, marginLeft: 6 }}>{hint}</span>}
    </label>
    {children}
    {error && <span style={{ fontSize: 11, color: "#e74c3c", marginTop: 1 }}>{error}</span>}
  </div>
);

/* ─── Photo upload with canvas compression ──────────────────── */
function PhotoUpload({ cardId, photo, onPhoto, onClear }) {
  const [compressing, setCompressing] = useState(false);
  const [info, setInfo]               = useState(null);
  const { show: showToast }           = useToast();

  const handleChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCompressing(true);
    setInfo(null);
    try {
      const result = await processPhoto(file);
      setInfo({ sizeKB: result.sizeKB, originalSizeKB: result.originalSizeKB, compressed: result.compressed });
      onPhoto(cardId, result.dataUrl);
    } catch (err) {
      showToast(err.message, "error");
      e.target.value = "";
    } finally {
      setCompressing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleChange}
        disabled={compressing}
        style={{
          width: "100%", padding: "9px 13px",
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 9, color: "rgba(240,238,248,0.6)",
          fontSize: 14, fontFamily: "DM Sans, sans-serif",
          outline: "none", cursor: compressing ? "not-allowed" : "pointer",
          boxSizing: "border-box",
        }}
      />

      {compressing && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#a89fff" }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #7C6FFF", borderTopColor: "transparent", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
          Compressing image…
        </div>
      )}

      {photo && !compressing && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={photo} alt="preview" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(124,111,255,0.4)", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#9fd45a", marginBottom: 2 }}>✓ Photo ready</div>
            {info && (
              <div style={{ fontSize: 11, color: "rgba(240,238,248,0.35)" }}>
                {info.compressed
                  ? `Compressed: ${info.originalSizeKB} KB → ${info.sizeKB} KB`
                  : `${info.sizeKB} KB`
                }
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            style={{ fontSize: 11, color: "#e74c3c", background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Single card accordion ─────────────────────────────────── */
function CardAccordion({ card, index, total, onChange, onRemove, errors, open, onToggle }) {
  const colorObj  = CARD_COLORS.find(c => c.id === card.cardColor) || CARD_COLORS[0];
  const hasErrors = Object.keys(errors).length > 0;
  const isComplete = card.name && card.bloodGroup && card.phone && card.shippingAddress && card.accepted && !hasErrors;
  const set = (name, val) => onChange(card._id, name, val);

  return (
    <div style={{
      border: `1px solid ${hasErrors ? "rgba(231,76,60,0.45)" : open ? "rgba(124,111,255,0.35)" : "rgba(255,255,255,0.09)"}`,
      borderRadius: 16, overflow: "hidden", transition: "border-color .2s",
    }}>

      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", background: open ? "rgba(124,111,255,0.08)" : "rgba(255,255,255,0.03)", border: "none", cursor: "pointer", textAlign: "left", borderBottom: open ? `1px solid ${BORDER}` : "none" }}
      >
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: colorObj.hex, border: "1px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#333", flexShrink: 0, overflow: "hidden" }}>
          {card.photo ? <img src={card.photo} alt="" style={{ width: 32, height: 32, objectFit: "cover" }} /> : index + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "#fff" }}>
            Card {index + 1}{card.name ? ` — ${card.name}` : ""}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {isComplete
              ? "✓ Complete"
              : hasErrors
              ? "⚠ Has errors — tap to fix"
              : card.name
              ? `${colorObj.label} · ${card.bloodGroup || "no blood group yet"}`
              : "Tap to fill in details"
            }
          </div>
        </div>
        {total > 1 && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onRemove(card._id); }}
            style={{ padding: "4px 9px", borderRadius: 7, background: "rgba(231,76,60,0.1)", border: "1px solid rgba(231,76,60,0.2)", color: "#e74c3c", cursor: "pointer", fontSize: 11, fontFamily: "DM Sans, sans-serif", flexShrink: 0 }}
          >
            Remove
          </button>
        )}
        <span style={{ color: MUTED, fontSize: 14, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>

      {/* Body */}
      {open && (
        <div style={{ padding: "18px 18px 22px", display: "flex", flexDirection: "column", gap: 12, background: "#080B18" }}>

          {/* Colour picker */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "rgba(240,238,248,0.6)", display: "block", marginBottom: 8 }}>Card Colour</label>
            <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
              {CARD_COLORS.map(c => (
                <button
                  key={c.id} type="button"
                  onClick={() => set("cardColor", c.id)}
                  title={c.label}
                  style={{ width: 28, height: 28, borderRadius: "50%", background: c.hex, border: "none", cursor: "pointer", flexShrink: 0, boxShadow: card.cardColor === c.id ? `0 0 0 2.5px #fff, 0 0 0 5px ${c.hex}` : "0 0 0 1px rgba(255,255,255,0.15)", transition: "box-shadow .15s" }}
                />
              ))}
              <span style={{ fontSize: 12, color: MUTED, marginLeft: 2 }}>{colorObj.label}</span>
            </div>
          </div>

          {/* ── BASIC INFO ── */}
          <FSectionLabel>Basic Info</FSectionLabel>

          <Row label="Full Name" required error={errors.name}>
            <Inp name="name" value={card.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Rahul Sharma" err={errors.name} />
          </Row>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Row label="Date of Birth" required error={errors.dob}>
              <Inp type="date" value={card.dob} onChange={e => set("dob", e.target.value)} err={errors.dob} style={{ colorScheme: "dark" }} />
            </Row>
            <Row label="Gender">
              <MedSelect options={GENDER_OPTIONS} value={card.gender} onChange={e => set("gender", e.target.value)} />
            </Row>
          </div>

          <Row label="Profile Photo" hint="(JPG/PNG/WebP, up to 10MB — auto-compressed)">
            <PhotoUpload
              cardId={card._id}
              photo={card.photo}
              onPhoto={(id, dataUrl) => onChange(id, "photo", dataUrl)}
              onClear={() => set("photo", "")}
            />
          </Row>

          {/* ── MEDICAL INFO ── */}
          <FSectionLabel>Medical Info</FSectionLabel>

          <Row label="Blood Group" required error={errors.bloodGroup}>
            <MedSelect options={BLOOD_GROUP_OPTIONS} value={card.bloodGroup} onChange={e => set("bloodGroup", e.target.value)} err={errors.bloodGroup} />
          </Row>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Row label="Diabetes Status">
              <MedSelect options={DIABETES_OPTIONS} value={card.diabetes} onChange={e => set("diabetes", e.target.value)} />
            </Row>
            <Row label="Blood Pressure">
              <MedSelect options={BLOOD_PRESSURE_OPTIONS} value={card.bloodPressure} onChange={e => set("bloodPressure", e.target.value)} />
            </Row>
          </div>

          <Row label="Home Address">
            <Inp value={card.address} onChange={e => set("address", e.target.value)} placeholder="Your residential address" />
          </Row>

          {/* ── EMERGENCY CONTACTS ── */}
          <FSectionLabel>Emergency Contacts</FSectionLabel>

          {[1, 2, 3].map(n => (
            <div key={n} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Row label={`Contact ${n} — Name & Relation`} required={n === 1} error={n === 1 ? errors.ec1Name : undefined}>
                <Inp
                  value={card[`ec${n}Name`]}
                  onChange={e => set(`ec${n}Name`, e.target.value)}
                  placeholder={n === 1 ? "e.g. Priya (Wife)" : "Optional"}
                  err={n === 1 ? errors.ec1Name : undefined}
                />
              </Row>
              <Row label={`Contact ${n} — Phone`} required={n === 1} error={n === 1 ? errors.ec1Phone : undefined}>
                <Inp
                  value={card[`ec${n}Phone`]}
                  onChange={e => set(`ec${n}Phone`, e.target.value)}
                  placeholder="+91 98765 43210"
                  err={n === 1 ? errors.ec1Phone : undefined}
                />
              </Row>
            </div>
          ))}

          {/* ── DELIVERY ── */}
          <FSectionLabel>Delivery Details</FSectionLabel>

          <Row label="Your WhatsApp / Phone" required error={errors.phone}>
            <Inp value={card.phone} onChange={e => set("phone", e.target.value)} placeholder="+91 98765 43210" err={errors.phone} />
          </Row>

          <Row label="Full Shipping Address" required error={errors.shippingAddress}>
            <Inp value={card.shippingAddress} onChange={e => set("shippingAddress", e.target.value)} placeholder="Flat no., Street, City, State" err={errors.shippingAddress} />
          </Row>

          <Row label="Pincode" required error={errors.pincode}>
            <Inp
              value={card.pincode}
              onChange={e => set("pincode", e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 226001"
              maxLength={6}
              inputMode="numeric"
              err={errors.pincode}
              style={{ maxWidth: 140 }}
            />
          </Row>

          {/* ── DISCLAIMER ── */}
          <div style={{ background: "rgba(124,111,255,0.06)", border: `1px solid ${errors.accepted ? "rgba(231,76,60,0.5)" : "rgba(124,111,255,0.2)"}`, borderRadius: 10, padding: "13px 14px", marginTop: 4 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={card.accepted}
                onChange={e => set("accepted", e.target.checked)}
                style={{ width: 17, height: 17, marginTop: 2, accentColor: "#7C6FFF", flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: "rgba(240,238,248,0.7)", lineHeight: 1.65 }}>
                I understand that DialHoop is an{" "}
                <strong style={{ color: "#fff" }}>identity and emergency contact tool only</strong>.
                DialHoop is not responsible for any medical decisions, treatments, or outcomes based on this card.
                <span style={{ color: "#e74c3c" }}> *</span>
              </span>
            </label>
            {errors.accepted && (
              <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 5, paddingLeft: 27 }}>
                You must accept this to continue
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── ORDER PAGE ────────────────────────────────────────────── */
export default function OrderPage({ onBack, price = 199 }) {
  const [cards, setCards]         = useState([EMPTY_CARD()]);
  const [openId, setOpenId]       = useState(null);
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast, show: showToast, hide: hideToast } = useToast();

  useEffect(() => { setOpenId(cards[0]._id); }, []);

  useEffect(() => {
    document.title = `Order DialHoop Card${cards.length > 1 ? "s" : ""} — ₹${price * cards.length}`;
  }, [cards.length, price]);

  const handleChange = (id, name, val) => {
    setCards(p => p.map(c => c._id === id ? { ...c, [name]: val } : c));
    setErrors(p => { const n = { ...p }; if (n[id]) delete n[id][name]; return n; });
  };

  const addCard = () => {
    const nc = EMPTY_CARD();
    setCards(p => [...p, nc]);
    setOpenId(nc._id);
    setTimeout(() => document.getElementById(`card-${nc._id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const removeCard = (id) => {
    setCards(p => {
      const next = p.filter(c => c._id !== id);
      if (openId === id && next.length) setOpenId(next[next.length - 1]._id);
      return next;
    });
    setErrors(p => { const n = { ...p }; delete n[id]; return n; });
  };

  const handleSubmit = async () => {
    /* Validate all cards */
    let allValid = true;
    const newErrors = {};
    cards.forEach(card => {
      const e = validateCard(card);
      if (Object.keys(e).length) { newErrors[card._id] = e; allValid = false; }
    });
    setErrors(newErrors);

    if (!allValid) {
      const firstId = Object.keys(newErrors)[0];
      setOpenId(firstId);
      showToast(`Fix the highlighted fields${cards.length > 1 ? " in all cards" : ""}`, "error");
      setTimeout(() => document.getElementById(`card-${firstId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      return;
    }

    setLoading(true);
    try {
      for (const card of cards) {
        await addDoc(collection(db, "contacts"), {
          name:             card.name.trim(),
          photo:            card.photo || "",
          gender:           card.gender || "",
          dob:              card.dob,
          address:          card.address.trim(),
          shippingAddress:  card.shippingAddress.trim(),
          pincode:          card.pincode.trim(),
          phone:            card.phone.trim(),
          diabetes:         card.diabetes || "",
          bloodPressure:    card.bloodPressure || "",
          bloodGroup:       card.bloodGroup,
          cardColor:        card.cardColor,
          memberId:         generateMemberId(),
          status:           "pending",
          amount:           price,
          emergencyContacts: [
            { name: card.ec1Name.trim(), phone: card.ec1Phone.trim() },
            { name: card.ec2Name.trim(), phone: card.ec2Phone.trim() },
            { name: card.ec3Name.trim(), phone: card.ec3Phone.trim() },
          ].filter(c => c.name),
          createdAt: serverTimestamp(),
        });
      }
      setSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      showToast("Failed to place order. Check your connection and try again.", "error");
    }
    setLoading(false);
  };

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#080B18", color: "#F0EEF8", fontFamily: "DM Sans, sans-serif" }}>
        <BgDecorations />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "24px 20px", position: "relative", zIndex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 54, marginBottom: 18 }}>🎉</div>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 26, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
            Request Submitted!
          </div>
          <div style={{ fontSize: 15, color: MUTED, maxWidth: 440, lineHeight: 1.8, marginBottom: 12 }}>
            {cards.length} card{cards.length > 1 ? "s" : ""} submitted. Our team will review and ship your card{cards.length > 1 ? "s" : ""} within 3–5 business days.
          </div>
          {cards.length > 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16, textAlign: "left" }}>
              {cards.map((c, i) => (
                <div key={c._id} style={{ fontSize: 13, color: MUTED }}>
                  Card {i + 1}: <span style={{ color: "#fff" }}>{c.name}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 12, color: "rgba(240,238,248,0.28)", marginBottom: 28 }}>
            Questions? support@dialhoop.com
          </div>
          <BtnPrimary onClick={onBack}>← Back to Home</BtnPrimary>
        </div>
        <GlobalStyles />
      </div>
    );
  }

  const total = cards.length * price;

  return (
    <div style={{ minHeight: "100vh", background: "#080B18", color: "#F0EEF8", fontFamily: "DM Sans, sans-serif" }}>
      <BgDecorations />
      <Nav right={
        <button
          onClick={onBack}
          disabled={loading}
          style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 100, padding: "8px 16px", color: MUTED, cursor: loading ? "not-allowed" : "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 13, opacity: loading ? 0.5 : 1 }}
        >
          ← Back
        </button>
      } />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 560, margin: "0 auto", padding: "80px 16px 60px" }}>

        {/* Page header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
            Order your card{cards.length > 1 ? "s" : ""}
          </div>
          <div style={{ fontSize: 14, color: MUTED }}>
            Fill in the details. We'll print, laminate and ship to your door.
          </div>
        </div>

        {/* Cart pill */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(124,111,255,0.1)", border: "1px solid rgba(124,111,255,0.22)", borderRadius: 12, padding: "11px 16px", marginBottom: 18, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>🛒</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{cards.length} card{cards.length > 1 ? "s" : ""}</span>
            <span style={{ fontSize: 13, color: MUTED }}>× ₹{price}</span>
          </div>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 18, fontWeight: 700, color: "#fff" }}>₹{total}</div>
        </div>

        {/* Card accordions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {cards.map((card, i) => (
            <div key={card._id} id={`card-${card._id}`}>
              <CardAccordion
                card={card} index={i} total={cards.length}
                onChange={handleChange}
                onRemove={removeCard}
                errors={errors[card._id] || {}}
                open={openId === card._id}
                onToggle={() => setOpenId(openId === card._id ? null : card._id)}
              />
            </div>
          ))}
        </div>

        {/* Add another card */}
        <button
          type="button"
          onClick={addCard}
          disabled={loading}
          style={{ width: "100%", marginTop: 12, padding: "13px", background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.18)", borderRadius: 12, color: MUTED, cursor: loading ? "not-allowed" : "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}
        >
          <span style={{ fontSize: 18 }}>+</span> Add another card (+₹{price})
        </button>

        {/* Order summary */}
        <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "16px 18px", marginTop: 16 }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 10 }}>Order Summary</div>
          {cards.map((c, i) => (
            <div key={c._id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: MUTED, padding: "2px 0" }}>
              <span>Card {i + 1}{c.name ? ` — ${c.name}` : ""}{c.bloodGroup ? ` · ${c.bloodGroup}` : ""}</span>
              <span style={{ color: "#fff" }}>₹{price}</span>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "space-between", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "#fff" }}>
            <span>Total</span><span>₹{total}</span>
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>Includes free shipping · No subscription</div>
        </div>

        {/* Submit button */}
        <BtnPrimary
          onClick={handleSubmit}
          disabled={loading}
          style={{ width: "100%", marginTop: 14, fontSize: 16, padding: "15px", opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
        >
          {loading ? "Submitting…" : `Submit Order${cards.length > 1 ? ` (${cards.length} cards)` : ""} — ₹${total} →`}
        </BtnPrimary>

        <div style={{ textAlign: "center", fontSize: 11, color: "rgba(240,238,248,0.28)", marginTop: 8 }}>
          Our team will contact you to arrange payment and shipping.
        </div>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={hideToast} />}
      <GlobalStyles />
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        select option { background: #0d1225; }
      `}</style>
    </div>
  );
}
